import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Popconfirm,
  Tag,
  Tooltip,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  SoundOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import dayjs from 'dayjs';
import { admin } from '../../services/api';

const { Title, Text } = Typography;

const PalavrasManagement = () => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const audioWatch = Form.useWatch('audio', form);

  const [loading, setLoading] = useState(false);
  const [palavras, setPalavras] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPalavra, setEditingPalavra] = useState(null);

  // Descoberta dinâmica de áudios usando Vite import.meta.glob
  // Buscamos os arquivos na pasta assets para descoberta em tempo de compilação
  const audioModules = import.meta.glob('../../assets/sound/*.{mp3,wav,ogg}', { eager: true });
  const availableAudios = Object.entries(audioModules).map(([fullPath, module]) => {
    const fileName = fullPath.split('/').pop();
    return {
      name: fileName,
      path: `/sound/${fileName}`, // Caminho estável para o banco
      moduleUrl: module.default || module // URL resolvida pelo Vite (importante para dev/build)
    };
  });

  useEffect(() => {
    loadPalavras();
  }, []);

  const loadPalavras = async () => {
    try {
      setLoading(true);
      const data = await admin.getPalavras();
      setPalavras(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar palavras:', error);
      message.error('Erro ao carregar lista de palavras');
    } finally {
      setLoading(false);
    }
  };

  const playAudio = (audioPath) => {
    if (!audioPath) return;

    try {
      // Se for um caminho relativo que começa com /sound, garantir que use o origin correto
      // Se já for uma URL (resolvida pelo Vite), usar como está
      let fullPath = audioPath;
      if (!audioPath.startsWith('http') && !audioPath.startsWith('blob:') && !audioPath.startsWith('data:')) {
        const baseUrl = window.location.origin;
        fullPath = `${baseUrl}${audioPath.startsWith('/') ? '' : '/'}${audioPath}`;
      }

      console.log('Tentando reproduzir áudio:', fullPath);

      const audio = new Audio();
      audio.src = fullPath;

      // Alguns navegadores exigem load() antes de play()
      audio.load();

      // Reiniciar áudio se já estiver tocando
      audio.currentTime = 0;

      audio.play().catch(err => {
        console.error('Erro ao reproduzir áudio:', err, fullPath);
        if (err.name === 'NotSupportedError') {
          message.error(`Erro: Formato não suportado ou arquivo não encontrado (${audioPath.split('/').pop()})`);
        } else if (err.name === 'NotAllowedError') {
          message.error('Reprodução de áudio bloqueada pelo navegador. Clique na página e tente novamente.');
        } else {
          message.error('Erro ao reproduzir áudio');
        }
      });
    } catch (err) {
      console.error('Erro ao criar objeto de áudio:', err);
      message.error('Erro ao inicializar player de áudio');
    }
  };

  const handleAdd = () => {
    setEditingPalavra(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPalavra(record);
    form.setFieldsValue({
      palavra: record.palavra,
      audio: record.audio
    });
    setModalVisible(true);
  };

  const handleDelete = async (palavra) => {
    try {
      await admin.deletePalavra(palavra);
      message.success('Palavra excluída com sucesso');
      loadPalavras();
    } catch (error) {
      console.error('Erro ao excluir palavra:', error);
      message.error(error.message || 'Erro ao excluir palavra');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingPalavra) {
        await admin.updatePalavra(editingPalavra.palavra, values);
        message.success('Palavra atualizada com sucesso');
      } else {
        await admin.createPalavra(values);
        message.success('Palavra criada com sucesso');
      }

      setModalVisible(false);
      loadPalavras();
    } catch (error) {
      console.error('Erro ao salvar palavra:', error);
      message.error(error.message || 'Erro ao salvar palavra');
    } finally {
      setLoading(false);
    }
  };

  const filteredPalavras = palavras.filter(item =>
    item.palavra.toLowerCase().includes(searchText.toLowerCase()) ||
    (item.usuario && item.usuario.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Palavra',
      dataIndex: 'palavra',
      key: 'palavra',
      sorter: (a, b) => a.palavra.localeCompare(b.palavra),
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Áudio',
      dataIndex: 'audio',
      key: 'audio',
      render: (audio) => audio ? (
        <Space>
          <Tag icon={<SoundOutlined />} color="cyan">
            {audio.split('/').pop()}
          </Tag>
          <Button
            type="text"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => playAudio(audio)}
          />
        </Space>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Usuário',
      dataIndex: 'usuario',
      key: 'usuario',
      sorter: (a, b) => (a.usuario || '').localeCompare(b.usuario || ''),
      render: (text) => <Tag color="blue">{text || 'Sistema'}</Tag>
    },
    {
      title: 'Data Ref.',
      dataIndex: 'data',
      key: 'data',
      sorter: (a, b) => new Date(a.data) - new Date(b.data),
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Última Atualização',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm:ss') : '-'
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Excluir palavra"
            description={`Tem certeza que deseja excluir a palavra "${record.palavra}"?`}
            onConfirm={() => handleDelete(record.palavra)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Excluir">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="palavras-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Gestío de Palavras</Title>
          <Text type="secondary">Gerencie as palavras para busca no campo 'Observações' da Nota Fiscal</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadPalavras} loading={loading}>
            Atualizar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nova Palavra
          </Button>
        </Space>
      </div>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Pesquisar palavra ou usuário..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredPalavras}
          rowKey="palavra"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} palavras`
          }}
        />
      </Card>

      <Modal
        title={editingPalavra ? 'Editar Palavra' : 'Nova Palavra'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="palavra"
            label="Palavra"
            rules={[
              { required: true, message: 'Por favor, insira a palavra' },
              { max: 100, message: 'A palavra deve ter no máximo 100 caracteres' }
            ]}
          >
            <Input placeholder="Digite a palavra..." />
          </Form.Item>

          <Form.Item
            label="Áudio para Notificação"
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Item name="audio" noStyle>
                <Select
                  placeholder="Selecione um áudio..."
                  allowClear
                  style={{ flex: 1 }}
                >
                  {availableAudios.map(audio => (
                    <Select.Option key={audio.path} value={audio.path}>
                      {audio.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => {
                  if (audioWatch) {
                    const audioObj = availableAudios.find(a => a.path === audioWatch);
                    playAudio(audioObj ? audioObj.moduleUrl : audioWatch);
                  }
                  else message.info('Selecione um áudio primeiro');
                }}
                disabled={!audioWatch}
              />
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PalavrasManagement;
